import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { 
  Phone, 
  PhoneIncoming, 
  PhoneOutgoing,
  Mail, 
  MessageSquare, 
  Calendar,
  Video,
  FileText,
  Plus,
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle2,
  XCircle,
  MinusCircle,
  PhoneMissed,
  Send,
  StickyNote,
  MessageCircle,
  Pencil,
  Trash2,
  MailCheck,
  MessagesSquare,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import RichEmailEditor from './RichEmailEditor';

// Types de contact disponibles
const contactTypes = [
  { value: 'call_outgoing', label: 'Appel sortant', icon: PhoneOutgoing, color: 'bg-blue-500', lightColor: 'bg-blue-100 text-blue-700', emoji: '📞' },
  { value: 'call_incoming', label: 'Appel entrant', icon: PhoneIncoming, color: 'bg-green-500', lightColor: 'bg-green-100 text-green-700', emoji: '📲' },
  { value: 'email', label: 'Email', icon: Mail, color: 'bg-amber-500', lightColor: 'bg-amber-100 text-amber-700', emoji: '✉️' },
  { value: 'email_ar', label: 'Courriel AR', icon: MailCheck, color: 'bg-orange-500', lightColor: 'bg-orange-100 text-orange-700', emoji: '📨' },
  { value: 'sms', label: 'SMS', icon: MessageSquare, color: 'bg-purple-500', lightColor: 'bg-purple-100 text-purple-700', emoji: '💬' },
  { value: 'chat', label: 'Chat', icon: MessagesSquare, color: 'bg-pink-500', lightColor: 'bg-pink-100 text-pink-700', emoji: '💭' },
  { value: 'meeting', label: 'RDV Agence', icon: Calendar, color: 'bg-indigo-500', lightColor: 'bg-indigo-100 text-indigo-700', emoji: '🏢' },
  { value: 'video_call', label: 'Visio', icon: Video, color: 'bg-cyan-500', lightColor: 'bg-cyan-100 text-cyan-700', emoji: '🎥' },
  { value: 'note', label: 'Note', icon: StickyNote, color: 'bg-slate-500', lightColor: 'bg-slate-100 text-slate-700', emoji: '📝' }
];

// Options de resultat
const outcomeOptions = [
  { value: 'positive', label: 'Positif', icon: CheckCircle2, color: 'text-green-600', bgColor: 'bg-green-100' },
  { value: 'neutral', label: 'Neutre', icon: MinusCircle, color: 'text-slate-500', bgColor: 'bg-slate-100' },
  { value: 'negative', label: 'Négatif', icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-100' },
  { value: 'no_answer', label: 'Pas de réponse', icon: PhoneMissed, color: 'text-amber-500', bgColor: 'bg-amber-100' }
];

const ContactHistoryWidget = ({ 
  entityId, 
  entityType = 'prospect',
  contactHistory = [],
  internalNotes = [],
  chatMessages = [],
  onContactAdded,
  onNoteAdded,
  showInternalNotes = true,
  showEmailButton = true,
  clientEmail = '',
  clientName = '',
  accentColor = '#3b82f6'
}) => {
  const [activeTab, setActiveTab] = useState('history');
  const [showModal, setShowModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [loadingChat, setLoadingChat] = useState(false);
  const [chatConversations, setChatConversations] = useState([]);
  
  const [formData, setFormData] = useState({
    contact_type: 'call_outgoing',
    summary: '',
    duration_minutes: '',
    outcome: 'neutral',
    next_action: '',
    next_action_date: ''
  });

  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Charger les conversations chat si c'est un client
  useEffect(() => {
    if (entityType === 'client' && entityId) {
      loadChatConversations();
    }
  }, [entityId, entityType]);

  const loadChatConversations = async () => {
    if (!entityId) return;
    setLoadingChat(true);
    try {
      // Get projects for this client
      const projectsRes = await api.get('/projects/', { params: { client_id: entityId } });
      const projects = projectsRes.data || [];
      
      // Get recent messages for each project
      const chatData = [];
      for (const project of projects.slice(0, 5)) { // Limit to 5 projects
        try {
          const messagesRes = await api.get(`/projects/${project.id}/messages`, { params: { limit: 10 } });
          const messages = messagesRes.data || [];
          if (messages.length > 0) {
            chatData.push({
              projectId: project.id,
              projectName: project.name,
              messages: messages.slice(0, 5), // Last 5 messages
              totalMessages: messages.length
            });
          }
        } catch (e) {
          console.error('Error loading messages for project:', project.id, e);
        }
      }
      setChatConversations(chatData);
    } catch (error) {
      console.error('Error loading chat:', error);
    } finally {
      setLoadingChat(false);
    }
  };

  const getContactTypeInfo = (type) => {
    return contactTypes.find(ct => ct.value === type) || contactTypes[8];
  };

  const getOutcomeInfo = (outcome) => {
    return outcomeOptions.find(o => o.value === outcome) || outcomeOptions[1];
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setFormData({ ...formData, contact_type: type.value });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.summary.trim()) {
      toast.error('Veuillez ajouter un résumé');
      return;
    }

    setSaving(true);
    try {
      const endpoint = entityType === 'prospect' 
        ? `/prospects/${entityId}/contact`
        : `/users/clients/${entityId}/contact`;
      
      const payload = {
        contact_type: formData.contact_type,
        summary: formData.summary,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        outcome: formData.outcome,
        next_action: formData.next_action || null,
        next_action_date: formData.next_action_date || null
      };

      await api.post(endpoint, payload);
      toast.success('Échange enregistré');
      setShowModal(false);
      setFormData({
        contact_type: 'call_outgoing',
        summary: '',
        duration_minutes: '',
        outcome: 'neutral',
        next_action: '',
        next_action_date: ''
      });
      setSelectedType(null);
      if (onContactAdded) onContactAdded();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Veuillez saisir une note');
      return;
    }

    setSavingNote(true);
    try {
      const endpoint = entityType === 'prospect' 
        ? `/prospects/${entityId}/notes`
        : `/users/clients/${entityId}/notes`;
      
      await api.post(endpoint, { content: noteText });
      toast.success('Note ajoutée');
      setNoteText('');
      if (onNoteAdded) onNoteAdded();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Erreur lors de l\'ajout de la note');
    } finally {
      setSavingNote(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Aujourd'hui ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Hier ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'long', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short',
      year: diffDays > 365 ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Merge contact history with chat messages for unified timeline
  const mergedHistory = [...contactHistory];
  
  // Add chat messages to history
  chatConversations.forEach(conv => {
    conv.messages.forEach(msg => {
      mergedHistory.push({
        id: `chat_${msg.id}`,
        contact_type: 'chat',
        summary: msg.content,
        created_at: msg.created_at,
        created_by_name: msg.sender_name || 'Utilisateur',
        project_name: conv.projectName,
        project_id: conv.projectId,
        is_chat: true
      });
    });
  });

  // Sort by date
  mergedHistory.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const displayedHistory = showAll ? mergedHistory : mergedHistory.slice(0, 8);

  return (
    <>
      <Card className="border-0 shadow-sm">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-2 border-b">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <TabsList className="h-9">
                <TabsTrigger value="history" className="text-sm" onClick={() => setActiveTab('history')}>
                  <Clock className="w-4 h-4 mr-1.5" />
                  Historique
                </TabsTrigger>
                {showInternalNotes && (
                  <TabsTrigger value="notes" className="text-sm" onClick={() => setActiveTab('notes')}>
                    <MessageCircle className="w-4 h-4 mr-1.5" />
                    Notes internes
                  </TabsTrigger>
                )}
                {chatConversations.length > 0 && (
                  <TabsTrigger value="chat" className="text-sm" onClick={() => setActiveTab('chat')}>
                    <MessagesSquare className="w-4 h-4 mr-1.5" />
                    Chat ({chatConversations.reduce((acc, c) => acc + c.messages.length, 0)})
                  </TabsTrigger>
                )}
              </TabsList>

              <div className="flex items-center gap-2">
                {/* Bouton unique pour ajouter un échange */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      size="sm" 
                      className="gap-1.5"
                      style={{ backgroundColor: accentColor }}
                      data-testid="add-contact-dropdown"
                    >
                      <Plus className="w-4 h-4" />
                      Nouvel échange
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {contactTypes.map((type) => {
                      const Icon = type.icon;
                      return (
                        <DropdownMenuItem 
                          key={type.value}
                          onClick={() => handleSelectType(type)}
                          className="cursor-pointer"
                          data-testid={`select-type-${type.value}`}
                        >
                          <div className={`w-8 h-8 rounded-full ${type.color} flex items-center justify-center mr-3`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <span>{type.label}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Bouton Envoyer email */}
                {showEmailButton && clientEmail && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowEmailModal(true)}
                    className="gap-1.5"
                    data-testid="send-email-btn"
                  >
                    <Mail className="w-4 h-4" />
                    Envoyer email
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-4">
            {/* Onglet Historique */}
            <TabsContent value="history" className="mt-0 space-y-4">
              {/* Historique timeline */}
              <div className="relative">
                {mergedHistory.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">Aucun échange enregistré</p>
                    <p className="text-sm mt-1">Cliquez sur "Nouvel échange" pour ajouter un échange</p>
                  </div>
                ) : (
                  <div className="space-y-0">
                    {/* Timeline line */}
                    <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />
                    
                    {displayedHistory.map((item, index) => {
                      const typeInfo = getContactTypeInfo(item.contact_type);
                      const Icon = typeInfo.icon;
                      const outcomeInfo = item.outcome ? getOutcomeInfo(item.outcome) : null;
                      const OutcomeIcon = outcomeInfo?.icon;

                      return (
                        <div key={item.id || index} className="relative pl-12 pb-4 group">
                          {/* Timeline dot */}
                          <div 
                            className={`absolute left-2 top-1 w-7 h-7 rounded-full ${typeInfo.color} flex items-center justify-center shadow-sm`}
                          >
                            <Icon className="w-3.5 h-3.5 text-white" />
                          </div>

                          <div className="bg-white border rounded-lg p-3 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge className={`${typeInfo.lightColor} text-xs`}>
                                    {typeInfo.label}
                                  </Badge>
                                  {item.duration_minutes && (
                                    <span className="text-xs text-slate-500">
                                      ⏱ {item.duration_minutes} min
                                    </span>
                                  )}
                                  {outcomeInfo && (
                                    <span className={`flex items-center gap-1 text-xs ${outcomeInfo.color}`}>
                                      <OutcomeIcon className="w-3 h-3" />
                                      {outcomeInfo.label}
                                    </span>
                                  )}
                                  {item.is_chat && item.project_name && (
                                    <Badge variant="outline" className="text-xs">
                                      {item.project_name}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-line">
                                  {item.summary}
                                </p>
                                {item.next_action && (
                                  <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-100 text-xs">
                                    <span className="font-medium text-amber-700">Prochaine action :</span>
                                    <span className="text-amber-600 ml-1">{item.next_action}</span>
                                    {item.next_action_date && (
                                      <span className="text-amber-500 ml-1">
                                        ({new Date(item.next_action_date).toLocaleDateString('fr-FR')})
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {item.created_by_name || 'Équipe'}
                              </span>
                              <span>{formatDate(item.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {mergedHistory.length > 8 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAll(!showAll)}
                    className="w-full mt-2"
                  >
                    {showAll ? (
                      <>
                        <ChevronUp className="w-4 h-4 mr-1" />
                        Voir moins
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-1" />
                        Voir les {mergedHistory.length - 8} échanges plus anciens
                      </>
                    )}
                  </Button>
                )}
              </div>
            </TabsContent>

            {/* Onglet Notes internes */}
            {showInternalNotes && (
              <TabsContent value="notes" className="mt-0 space-y-4">
                {/* Formulaire d'ajout de note */}
                <div className="flex gap-2">
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Ajouter une note interne (visible uniquement par l'équipe)..."
                    className="resize-none"
                    rows={2}
                  />
                  <Button 
                    onClick={handleAddNote} 
                    disabled={savingNote || !noteText.trim()}
                    className="shrink-0"
                  >
                    {savingNote ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                {/* Liste des notes */}
                <div className="space-y-3">
                  {internalNotes.length === 0 ? (
                    <div className="text-center py-6 text-slate-500">
                      <StickyNote className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">Aucune note interne</p>
                    </div>
                  ) : (
                    internalNotes.map((note, index) => (
                      <div 
                        key={note.id || index} 
                        className="p-3 bg-yellow-50 border border-yellow-100 rounded-lg"
                      >
                        <p className="text-sm text-slate-700 whitespace-pre-line">{note.content}</p>
                        <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {note.created_by_name || 'Équipe'}
                          </span>
                          <span>{formatDate(note.created_at)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            )}

            {/* Onglet Chat */}
            {chatConversations.length > 0 && (
              <TabsContent value="chat" className="mt-0 space-y-4">
                {loadingChat ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" />
                    <p className="text-sm text-slate-500 mt-2">Chargement des conversations...</p>
                  </div>
                ) : (
                  chatConversations.map((conv) => (
                    <div key={conv.projectId} className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 border-b flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessagesSquare className="w-4 h-4 text-slate-500" />
                          <span className="font-medium text-sm">{conv.projectName}</span>
                          <Badge variant="outline" className="text-xs">
                            {conv.totalMessages} messages
                          </Badge>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => window.open(`/projects/${conv.projectId}?tab=chat`, '_blank')}
                          className="h-7 text-xs"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Voir le chat
                        </Button>
                      </div>
                      <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
                        {conv.messages.map((msg) => (
                          <div 
                            key={msg.id} 
                            className="text-sm p-2 bg-slate-50 rounded"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-xs text-slate-600">
                                {msg.sender_name || 'Utilisateur'}
                              </span>
                              <span className="text-xs text-slate-400">
                                {formatDate(msg.created_at)}
                              </span>
                            </div>
                            <p className="text-slate-700 text-sm line-clamp-2">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>
            )}
          </CardContent>
        </Tabs>
      </Card>

      {/* Modal pour ajouter un échange */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedType && (
                <>
                  <div className={`w-8 h-8 rounded-full ${selectedType.color} flex items-center justify-center`}>
                    <selectedType.icon className="w-4 h-4 text-white" />
                  </div>
                  Nouvel échange - {selectedType.label}
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Type de contact (si on veut changer) */}
            <div>
              <Label>Type d'échange</Label>
              <Select 
                value={formData.contact_type} 
                onValueChange={(v) => {
                  setFormData({ ...formData, contact_type: v });
                  setSelectedType(contactTypes.find(t => t.value === v));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {contactTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Résumé */}
            <div>
              <Label>Résumé de l'échange *</Label>
              <Textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder="Décrivez l'échange..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Durée (pour appels) */}
              {['call_outgoing', 'call_incoming', 'video_call', 'meeting'].includes(formData.contact_type) && (
                <div>
                  <Label>Durée (minutes)</Label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                    placeholder="15"
                  />
                </div>
              )}

              {/* Résultat */}
              <div>
                <Label>Résultat</Label>
                <Select 
                  value={formData.outcome} 
                  onValueChange={(v) => setFormData({ ...formData, outcome: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {outcomeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className={`w-4 h-4 ${opt.color}`} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Prochaine action */}
            <div>
              <Label>Prochaine action</Label>
              <Input
                value={formData.next_action}
                onChange={(e) => setFormData({ ...formData, next_action: e.target.value })}
                placeholder="Ex: Rappeler pour confirmation"
              />
            </div>

            {/* Date prochaine action */}
            {formData.next_action && (
              <div>
                <Label>Date de la prochaine action</Label>
                <Input
                  type="date"
                  value={formData.next_action_date}
                  onChange={(e) => setFormData({ ...formData, next_action_date: e.target.value })}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rich Email Editor */}
      <RichEmailEditor
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recipientEmail={clientEmail}
        recipientName={clientName}
        clientId={entityId}
        entityType={entityType}
        onEmailSent={onContactAdded}
      />
    </>
  );
};

export default ContactHistoryWidget;
